import { Orders } from '@database/types';

export enum MatchBasis {
  COLOR = 'COLOR',
  MODEL = 'MODEL',
  PRICING = 'PRICING',
  SIZE = 'SIZE',
  SKU = 'SKU',
}

export enum ProductSelectionSource {
  AI_FINDER = 'AI_FINDER',
  // The customer's own product is discontinued or out of stock, so they accepted a near-equivalent
  // the store is willing to send instead (ENG-5874). Distinct from AI_FINDER: this is knowingly not
  // the item they owned, and the reviewing agent needs to see that.
  CLOSE_MATCH = 'CLOSE_MATCH',
  NOT_FOUND = 'NOT_FOUND',
  SEARCH = 'SEARCH',
}

/**
 * The finder answers 200 with no candidates for several outcomes that are not "we searched and found
 * nothing" — no LLM provider, the daily token ceiling, a catalog lookup that failed. Only OK means the
 * search actually ran, so the UI can tell a real zero-result from an unavailable finder.
 */
export enum FinderStatus {
  OK = 'OK',
  UNAVAILABLE = 'UNAVAILABLE',
}

export const BASIS_LABEL: Record<MatchBasis, string> = {
  [MatchBasis.COLOR]: 'Color',
  [MatchBasis.MODEL]: 'Model',
  [MatchBasis.PRICING]: 'Pricing',
  [MatchBasis.SIZE]: 'Size',
  [MatchBasis.SKU]: 'SKU',
};

export interface ProductFinderResult {
  candidates: ProductCandidate[];
  status: FinderStatus;
  /**
   * The kind of thing the customer said they own — "snowboard", "tumbler" — or null when they gave
   * only a brand, colour or size. Read from their words alone, never inferred from the catalog.
   *
   * The replacement picker will not offer substitutes without it: retrieval always returns its
   * nearest rows, and for a brand-only query the nearest rows are just whatever that brand sells.
   */
  productType: string | null;
}

/** A product line read off the customer's uploaded receipt, used to fill in the finder's form. */
export interface ReceiptItem {
  name: string;
  description: string | null;
  /** What the customer paid for this line. Null whenever the receipt did not plainly say. */
  price?: number | null;
}

export interface ReceiptReadResult {
  items: ReceiptItem[];
  status: FinderStatus;
}

export interface ProductCandidate {
  basis: MatchBasis[];
  // Null when the platform reports no stock information for the row, which is not the same as being
  // out of stock — only an explicit false rules a product out as a replacement.
  inStock?: boolean | null;
  matchReason?: string | null;
  name: string;
  /** The product line the row belongs to, e.g. "Salomon Rancho Snowboard" for its brown colourway. */
  parentName?: string | null;
  price: number | null;
  productImage: string | null;
  productWarranty?: Orders.ProductWarranty[];
  score: number;
  sku: string | null;
  // Legacy catalog rows hold the variant id in `sourceProductId`; only the parent knows the product
  // id, and `resolveProductSourceIds` needs it to tell the two shapes apart until ENG-5825 backfills.
  sourceParentProductId?: string | null;
  sourceProductId: string;
  sourceVariantId: string | null;
  status: Orders.ProductStatus;
}
